import { getUserSettingsLocale } from '../store/auth.selectors';
import { AuthStore } from '../store/auth.state';
import { CurrencyPipe } from '@angular/common';
import {
  Inject,
  InjectionToken,
  Optional,
  Pipe
} from '@angular/core';
import { findById } from '@ppl/utils/array.utils';
import type { PipeTransform } from '@angular/core';
import type { Subscription } from 'rxjs';
import type { ID } from '@ppl/graphql-user-api';

type CurrencySelector = (state: any) => CurrencyObject;
type CurrenciesSelector = (state: any) => CurrencyObject[];

interface CurrencyObject {
  code: string;
  id: ID;
}

type CurrencyFormattingSelector = (state: any) => 'ConvertedValue' | 'RecordValue' | 'RecordValueConvertedValue';
type CurrencyDisplayOptionSelector = (state: any) => 'Code' | 'Symbol';

interface ValueAsObject {
  baseValue: number;
  convertedValue: number;
  convertedValueCurrencyId: string;
  valueForeign: number;
  currencyId: string;
}

export const CURRENCIES_SELECTOR = new InjectionToken<CurrencySelector>('CurrenciesSelector');
export const DEFAULT_CURRENCY_SELECTOR = new InjectionToken<CurrencySelector>('DefaultCurrencySelector');
export const CURRENCY_FORMATTING_SELECTOR = new InjectionToken<CurrencySelector>('CurrencyFormattingSelector');
export const CURRENCY_DISPLAY_OPTION_SELECTOR = new InjectionToken<CurrencySelector>('CurrencyDisplayOptionSelector');

@Pipe({
    name: 'pplCurrency',
    standalone: false
})
export class PplCurrencyPipe implements PipeTransform {
  locale: string;
  localeSubscription: Subscription;

  constructor(
    private currencyPipe: CurrencyPipe,
    private store: AuthStore,
    @Optional() @Inject(CURRENCIES_SELECTOR) private currenciesSelector: CurrenciesSelector,
    @Optional() @Inject(DEFAULT_CURRENCY_SELECTOR) private defaultCurrencySelector: CurrencySelector,
    @Optional() @Inject(CURRENCY_FORMATTING_SELECTOR) private currencyFormattingSelector: CurrencyFormattingSelector,
    @Optional() @Inject(CURRENCY_DISPLAY_OPTION_SELECTOR) private currencyDisplayOptionSelector: CurrencyDisplayOptionSelector,
  ) {
    this.localeSubscription = this.store.select(getUserSettingsLocale(true)).subscribe(locale => this.locale = locale);
  }

  transform(value: number | ValueAsObject, currencyCode?: string, currencySymbol?: 'Code' | 'Symbol', digitsInfo?: string) {
    if (!currencySymbol) {
      if (this.currencyDisplayOptionSelector) {
        currencySymbol = this.store.get(this.currencyDisplayOptionSelector);
      } else {
        currencySymbol = 'Symbol';
      }
    }

    if (typeof value === 'number') {
      if (!currencyCode && this.defaultCurrencySelector) {
        const defaultCurrency = this.store.get(this.defaultCurrencySelector);

        if (defaultCurrency) {
          currencyCode = defaultCurrency.code;
        }
      }

      return this.transformValue(value, currencyCode, currencySymbol, digitsInfo);
    } else if (value) {
      const currencies = this.store.get(this.currenciesSelector);
      const currencyFormatting = this.store.get(this.currencyFormattingSelector);

      const recordValue = () => this.transformValue(value.valueForeign, findById(currencies, value.currencyId)?.code, currencySymbol, digitsInfo);
      const convertedValue = () => this.transformValue(value.convertedValue, findById(currencies, value.convertedValueCurrencyId)?.code, currencySymbol, digitsInfo);

      switch (currencyFormatting) {
        case 'ConvertedValue':
          return convertedValue();
        case 'RecordValue':
          return recordValue();
        case 'RecordValueConvertedValue':
          if (value.convertedValueCurrencyId !== value.currencyId) {
            return `${recordValue()} (${convertedValue()})`;
          }

          return recordValue();
      }
    }

    return '';
  }

  private transformValue(value: number, currencyCode: string | undefined, currencySymbol: 'Code' | 'Symbol', digitsInfo?: string) {
    if (!currencyCode) {
      console.warn(`PplCurrencyPipe: Currency code not specified, defaulting to ${FallbackCurrencyCode}`);
      currencyCode = FallbackCurrencyCode;
    }

    if (currencyCode === 'XXX') {
      return this.currencyPipe.transform(value, '', '');
    }

    return this.currencyPipe.transform(
      value,
      currencyCode,
      (currencySymbol === 'Code') ? `${currencyCode} ` : 'symbol-narrow',
      digitsInfo,
      this.locale
    );
  }

}

const FallbackCurrencyCode = 'USD';
